// cookAudio — cook timer + step chimes routed through BoodaVoice's
// engine, NOT Web Audio, NOT HTML5 <audio>. ALL speech-shaped audio
// goes through the engine's chimePlayer / narrationPlayer →
// mainMixerNode → outputNode + VPIO on inputNode. That's the only
// way iOS's voice-processing IO can subtract our own audio from the
// mic feed; HTML5 / Web Audio go through a separate audio path that
// VPIO can't AEC, which causes feedback into the recognizer AND
// silent chimes when the SR session has the audio hardware.
//
// Engine cases live in BoodaVoiceEngine.chimeBuffer:
//   "wake"     — single 880Hz chime when "Hey Booda" fires
//   "action"   — two-tone confirmation when a command is heard
//   "timer"    — three-note rising chime (soft) — legacy, unused
//   "alarm"    — RoundRobinAlarm.caf, same loud loop the Live Activity
//                pushes to the lock screen. Used for cook-timer end.
//   "stepDone" — soft single-note ping for step advance
//
// On non-iOS (web preview, Android), BoodaVoice.playChime is a noop.
// That's fine — these chimes are a hands-free hot-kitchen affordance,
// not core to the cook flow. If a non-iOS path ever needs audible
// chimes, add a separate fallback rather than re-introducing Web
// Audio here.
//
// Haptic: navigator.vibrate — pattern depends on chime type. Graceful
// no-op on iOS Safari (no vibrate).
//
// Opt-out: localStorage["mise.cookAudio.enabled"] = "false" silences
// chimes (engine call still no-ops; haptics still fire). Default on.

import { BoodaVoice } from "./boodaVoice";

function audioEnabled() {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem("mise.cookAudio.enabled") !== "false";
  } catch {
    return true;
  }
}

// Kept for compatibility with call sites that wired it in for the
// old Web Audio gesture-priming. With the engine path there's nothing
// to prime — the engine is started by useVoiceCommands when the user
// enables voice mode, and chimes piggyback on whatever session it
// holds. No-op.
export function primeCookAudio() {
  /* no-op — engine handles its own session */
}

// Timer-end alarm. Plays the same RoundRobinAlarm.caf the Live Activity
// pushes to the lock screen, looped enough times that the user actually
// notices from across the kitchen. Stop with `stopTimerChime()` when
// the user dismisses (CookTimers' Restart / Next / dismiss paths).
const TIMER_ALARM_REPEAT = 6;

export function playTimerChime() {
  if (!audioEnabled()) return;
  BoodaVoice.playChime("alarm", { repeatCount: TIMER_ALARM_REPEAT }).catch((e) => {
    console.warn("[cookAudio] playChime(alarm) failed:", e?.message || e);
  });
  if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
    try {
      navigator.vibrate([400, 200, 400, 200, 400, 200, 400, 200, 400, 200, 400]);
    } catch { /* ignore */ }
  }
}

export function stopTimerChime() {
  BoodaVoice.stopChime().catch((e) => {
    console.warn("[cookAudio] stopChime failed:", e?.message || e);
  });
}

export function playStepCompleteChime() {
  if (!audioEnabled()) return;
  BoodaVoice.playChime("stepDone").catch((e) => {
    console.warn("[cookAudio] playChime(stepDone) failed:", e?.message || e);
  });
  if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
    try { navigator.vibrate(40); } catch { /* ignore */ }
  }
}

export function setCookAudioEnabled(enabled) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem("mise.cookAudio.enabled", enabled ? "true" : "false");
  } catch { /* quota / privacy-mode */ }
}

export function isCookAudioEnabled() {
  return audioEnabled();
}
